/**
 * Environment configuration
 *
 * Configuration priority:
 * 1. Runtime config from window.__RUNTIME_CONFIG__ (client-side)
 * 2. process.env (server-side)
 * 3. import.meta.env (build-time)
 * 4. Default fallback values
 *
 * Kubernetes env vars (ConfigMaps) are available via process.env on the server
 * and via runtime-config.js on the client.
 */

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: Record<string, string>
  }
}

const getRuntimeConfig = (): Record<string, string> => {
  // Client-side: read from window.__RUNTIME_CONFIG__
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) {
    return window.__RUNTIME_CONFIG__
  }
  return {}
}

const getServerEnv = (key: string): string | undefined => {
  // Server-side: read from process.env
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key]
  }
  return undefined
}

export const env = {
  get apiBaseUrl() {
    // Use runtime config, env var, or default to localhost in development
    const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development'
    const defaultUrl = isDev ? 'http://localhost:8080/api' : 'https://api.wasilah.net/api'

    // Priority: runtime config > env vars > default
    // Runtime config is loaded from /runtime-config.js (set at container startup)
    const runtimeConfig = getRuntimeConfig().PUBLIC_API_BASE_URL
    const envVar = import.meta.env.PUBLIC_API_BASE_URL || import.meta.env.VITE_API_BASE_URL

    // In development, if env var is set to a remote URL (not localhost), use it
    // Otherwise, always use localhost:8080 for local development
    let result: string
    if (isDev) {
      // In dev mode, prefer localhost unless explicitly set to a remote URL
      if (runtimeConfig && runtimeConfig.startsWith('http://localhost:8080')) {
        result = runtimeConfig
      } else if (envVar && envVar.startsWith('http://localhost:8080')) {
        result = envVar
      } else if (runtimeConfig) {
        result = runtimeConfig
      } else if (envVar && envVar.startsWith('http')) {
        // Remote URL explicitly set - use it
        result = envVar
      } else {
        // Default to localhost for local development
        result = defaultUrl
      }
    } else {
      // Production: use runtime config, env var, or default
      result = runtimeConfig || envVar || defaultUrl
    }

    // Debug logging (only in dev mode) - disabled

    return result
  },
  get keycloakUrl() {
    return (
      getRuntimeConfig().PUBLIC_KEYCLOAK_URL ||
      getServerEnv('PUBLIC_KEYCLOAK_URL') ||
      import.meta.env.PUBLIC_KEYCLOAK_URL ||
      import.meta.env.VITE_KEYCLOAK_URL ||
      ''
    )
  },
  get keycloakRealm() {
    return (
      getRuntimeConfig().PUBLIC_KEYCLOAK_REALM ||
      getServerEnv('PUBLIC_KEYCLOAK_REALM') ||
      import.meta.env.PUBLIC_KEYCLOAK_REALM ||
      import.meta.env.VITE_KEYCLOAK_REALM ||
      ''
    )
  },
  get keycloakClientId() {
    return (
      getRuntimeConfig().PUBLIC_KEYCLOAK_CLIENT_ID ||
      getServerEnv('PUBLIC_KEYCLOAK_CLIENT_ID') ||
      import.meta.env.PUBLIC_KEYCLOAK_CLIENT_ID ||
      import.meta.env.VITE_KEYCLOAK_CLIENT_ID ||
      ''
    )
  },
  get isDevelopment() {
    return import.meta.env.DEV
  },
  get isProduction() {
    return import.meta.env.PROD
  },
  /**
   * Dev mode: bypass authentication for local development ONLY.
   * Guarded by the build-time constant import.meta.env.DEV: in production builds this is a
   * constant `false`, no runtime configuration or storage value can enable it.
   */
  get devBypassAuth(): boolean {
    if (!import.meta.env.DEV) return false
    const bypass =
      getRuntimeConfig().PUBLIC_DEV_BYPASS_AUTH ||
      import.meta.env.PUBLIC_DEV_BYPASS_AUTH ||
      import.meta.env.VITE_DEV_BYPASS_AUTH ||
      ''
    return bypass === 'true' || !env.keycloakUrl
  },
} as const
